import { Descriptions, Select } from 'antd';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { IExamSemester, IGetExamInfo, ISkillExam, ITestType, InitialSkillExam } from '../../../models/testCenter.model';
import { RootState } from '../../../store/rootReducer';
import Button from '../../Button';
import FileUpload from '../../FileUpload';
import useNotification from '../../../hooks/useNotification';
import useAxios from '../../../hooks/useAxios';
import {
  createExam,
  getExamInfo,
  publishExam,
  updateExam,
} from '../../../api/testCenter.api';
import { IGetSemesterMonths } from '../../../models/semesters.model';
import { getSemesterMonths } from '../../../api/semesters.api';
import Loader from '../../Spinner';
import ErrorResponse from '../../ErrorResponse';
import { CgCloseR } from 'react-icons/cg';
import ConfirmModal from '../../modals/ConfirmModal';
import SelectSkillExamModal from './modals/SelectSkillExamModal';
import useAuth from '../../../hooks/useAuth';
import { getReverseYears } from '../../../lib/util';
import SelectEduStep from '../../SelectEduStep';
import styled from 'styled-components';
import _ from 'lodash';

const Block = styled.div`
  .skill-exam-form {
    margin: 5px 0;
    display: flex;

    input {
      width: 80px;
    }
  }
`;

interface IProps {
  examCode: number | undefined;
  setExamCode: (examCode: number) => void;
  getExamData: () => void;
  typeData: ITestType[];
  examSemesterData: IExamSemester[];
  setIsPublished: (hasPublished: boolean) => void;
}

const TestExam: FC<IProps> = ({
  examCode,
  setExamCode,
  getExamData,
  typeData,
  examSemesterData,
  setIsPublished,
}) => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // TE0001 = 시험구분
  const TE0001 = commonCodes.find(
    (item) => item.commonCode === 'TE0001',
  )?.detailCode;
  // SY0005 = 연도
  const SY0005 = commonCodes.find(
    (item) => item.commonCode === 'SY0005',
  )?.detailCode;
  // PR0002 = 학기유형
  const PR0002 = commonCodes.find((item) => item.commonCode === 'PR0002')?.detailCode;
  const { infoNotification, successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetExamInfo>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetSemesterMonths>();
  const { data: submitRes, isLoading: submitIsLoading, error: submitError, execute: submitExecute } = useAxios<IGetExamInfo>();

  const [eduStepCode, setEduStepCode] = useState('');
  const [examDiv, setExamDiv] = useState<string>();
  const [examTypeCode, setExamTypeCode] = useState<number>();
  const [examTypes, setExamTypes] = useState<ITestType[]>([]);
  const [skillExams, setSkillExams] = useState<ISkillExam[]>([_.clone(InitialSkillExam)]);
  const [examName, setExamName] = useState('');
  const [year, setYear] = useState<string>();
  const [semesterCode, setSemesterCode] = useState<number>();
  const [examSemesterList, setExamSemesterList] = useState<IExamSemester[]>([]);
  const [testSeq, setTestSeq] = useState<number>();
  const [openYn, setOpenYn] = useState('N');
  const [files, setFiles] = useState<any>([null]);
  const [attachFile, setAttachFile] = useState<any>();
  const [attachFileCode, setAttachFileCode] = useState<number|null>(null);
  const [memo, setMemo] = useState('');

  const [publishExamVisible, setPublishExamVisible] = useState(false);
  const [selectSkillExamVisible, setSelectSkillExamVisible] = useState(false);
  const [skillExamIndex, setSkillExamIndex] = useState<number>();
  const [semesterType, setSemesterType] = useState("RG");

  useEffect(() => {
    if (examCode) {
      execute(getExamInfo(examCode));
    } else {
      setEduStepCode('EM');
      setExamDiv(undefined);
      setExamTypeCode(undefined);
      setSkillExams([_.clone(InitialSkillExam)]);
      setExamName('');
      setYear(undefined);
      setSemesterCode(undefined);
      setTestSeq(undefined);
      setOpenYn('N');
      setFiles([null]);
      setAttachFile(undefined);
      setAttachFileCode(null);
      setMemo('');
      setIsPublished(false);
    }
  }, [examCode]);

  useEffect(() => {
    if (res) {
      const data = res.data;
      setEduStepCode(data.eduStepCode);
      setExamDiv(data.examDiv);
      onSetExamTypes(data.eduStepCode, data.examDiv, data.examTypeCode);
      if (data.writingExamReqDtoList?.length) {
        setSkillExams(data.writingExamReqDtoList);
      } else {
        setSkillExams([_.clone(InitialSkillExam)]);
      }
      setExamName(data.examName);
      setYear(data.year);
      onSetSemesterList(data.year, data.semesterCode, data.semesterType);
      if(data.semesterType === 'SC') {
        setTestSeq(undefined);
      } else {
        setTestSeq(data.testSeq);
      }
      setOpenYn(data.openYn);
      setFiles([null]);
      setAttachFile(data.attachFile);
      setAttachFileCode(data.attachFileCode);
      setMemo(data.memo);
      setIsPublished(data.openYn === 'Y');
      setSemesterType(data.semesterType);
    }
  }, [res]);

  useEffect(() => {
    if (submitRes) {
      successNotification(t('common.message.save1'));
      getExamData();
      setExamCode(submitRes.data.examCode);
      if (examCode) {
        execute(getExamInfo(submitRes.data.examCode));
      }
      setPublishExamVisible(false);
    }
  }, [submitRes]);

  useEffect(() => {
    if (semesterCode) {
      execute2(getSemesterMonths(String(semesterCode)));
    }
  }, [semesterCode]);

  const onSetExamTypes = (
    eduStepCode: string,
    examDiv: string|undefined,
    examTypeCode?: number,
  ) => {
    const newExamTypes = typeData.filter(type => type.eduStepCode === eduStepCode && type.examDiv === examDiv);
    setExamTypes(newExamTypes);
    setExamTypeCode(examTypeCode ? examTypeCode : undefined);
  };

  const onSetSkillExamScore = (index: number, key: 'srtScore'|'endScore', value: string) => {
    if (value && !/^[0-9]*$/.test(value)) {
      return;
    }

    const _skillExams = [...skillExams];
    _skillExams[index][key] = value;
    setSkillExams(_skillExams);
  };

  const setSkillExam = (speakingExamCode: number, speakingExamName: string) => {
    if (typeof skillExamIndex !== 'number') return;
    const _skillExams = [...skillExams];
    _skillExams[skillExamIndex].speakingExamCode = speakingExamCode;
    _skillExams[skillExamIndex].speakingExamName = speakingExamName;
    setSkillExams(_skillExams);
  };

  const addSkillExam = () => {
    const _skillExams = [...skillExams];
    _skillExams.push(_.clone(InitialSkillExam));
    setSkillExams(_skillExams);
  };

  const deleteSkillExam = (index: number) => {
    const _skillExams = [...skillExams];
    _skillExams.splice(index, 1);
    setSkillExams(_skillExams);
  };

  const onSetSemesterList = (year: string, semesterCode?: number, dSemesterType?: string) => {
    let list = examSemesterData.filter((item) => item.learningYearCode === year);

    if(!dSemesterType) {
      dSemesterType = semesterType;
    }

    if(dSemesterType) {
      list = list.filter(item => item.semesterType === dSemesterType);
    }

    setExamSemesterList(list);
    setSemesterCode(semesterCode ? semesterCode : undefined);
  }

  const onSetSemesterListByType = (semesterType: string, semesterCode?: number) => {
    let list = examSemesterData.filter((item) => item.semesterType === semesterType);

    if(year) {
      list = list.filter(item => item.learningYearCode === year);
    }

    if(semesterType === 'SC') {
      setTestSeq(undefined);
    }

    setExamSemesterList(list);
    setSemesterCode(semesterCode ? semesterCode : undefined);
  }
  const onSetFile = (index: number, file: any) => {
    const _files = [...files];
    _files[index] = file;
    setFiles(_files);
  };

  const onAddFile = () => {
    const _files = [...files];
    _files.push(null);
    setFiles(_files);
  };

  const onDeleteFile = (index: number) => {
    const _files = [...files];
    _files.splice(index, 1);
    setFiles(_files);
  };

  const onDeleteAttachedFile = (index: number) => {
    const _attachFile = [...attachFile];
    _attachFile[index].originalName = null;
    setAttachFile(_attachFile);
  };

  const onSubmit = () => {
    if (!examDiv) {
      return infoNotification(t('cbt.test.message1'));
    }
    if (!examTypeCode) {
      return infoNotification(t('cbt.test.message2'));
    }
    if (!examName) {
      return infoNotification(t('cbt.test.message3'));
    }
    if (!year) {
      return infoNotification(t('cbt.test.message4'));
    }
    if (!semesterCode) {
      return infoNotification(t('class.class.message4'));
    }
    if (!testSeq && semesterType === 'RG') {
      return infoNotification(t('cbt.test.message5'));
    }

    const form = new FormData();
    form.append('eduStepCode', eduStepCode);
    form.append('examDiv', examDiv);
    form.append('examTypeCode', String(examTypeCode));
    form.append('semesterCode', String(semesterCode));
    form.append('examName', examName);
    form.append('year', year);
    if(semesterType === 'RG') {
      form.append('testSeq', String(testSeq));
    }
    form.append('memo', memo);

    const skillExamReq: ISkillExam[] = [];    
    if (eduStepCode === 'EM') {
      if (skillExams[0]?.speakingExamCode) {
        skillExamReq.push({
          speakingExamCode: skillExams[0].speakingExamCode,
          srtScore: 0,
          endScore: 100,
        });
      }
    } else {
      skillExams.forEach((item: ISkillExam) => {
        if (
          item.speakingExamCode &&
          (item.srtScore || item.srtScore === 0) &&
          (item.endScore || item.endScore === 0) 
        ) {
          skillExamReq.push(item);
        }
      });
    }

    if (skillExamReq.length) {
      form.append('writingExamReqDtoList', JSON.stringify(skillExamReq));
    }

    if (examCode) {
      // 수정 시 파일 처리
      if (attachFileCode) {
        form.append('attachFileCode', String(attachFileCode));
      }

      if (attachFile) {
        // 기존 첨부파일 있음
        let hasNewFiles = false;
        files.forEach((item: any) => {
          if (item) hasNewFiles = true;
        });

        let isAllDeleted = true;
        let hasDeleted = false;
        attachFile.forEach((item: any) => {
          if (item.originalName) {
            isAllDeleted = false;
          } else {
            hasDeleted = true;
            form.append('deleteFiles', item.fileSeq);
          }
        });

        if (hasNewFiles) form.append('fileStatus', 'update');
        else if (isAllDeleted) form.append('fileStatus', 'delete');
        else if (hasDeleted) form.append('fileStatus', 'update');
        else form.append('fileStatus', 'keep');
      } else {
        // 기존 첨부파일 없음
        if (files.length > 0 && files[0] !== null) {
          // 추가 첨부파일 있음
          form.append('fileStatus', 'create');
        } else {
          // 추가 첨부파일 없음
          form.append('fileStatus', 'keep');
        }
      }

      if (files.length > 0) {
        files.forEach((item: any) => {
          if (item !== null) form.append('newFiles', item);
        });
      }
    } else {
      // 생성 시 파일 처리
      if (files.length > 0) {
        files.forEach((item: any) => {
          if (item !== null) form.append('files', item);
        });
      }
    }

    examCode
      ? submitExecute(updateExam(examCode, form))
      : submitExecute(createExam(form));
  };

  const onOpenExam = (_openYn: string) => {
    if (!examCode) return;
    submitExecute(publishExam(examCode, { openYn: _openYn }));
  };

  return (
    <Block>
      {isLoading || submitIsLoading || isLoading2 ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={submitError} />
      <div className='comm-table__area rowType'>
      <Descriptions bordered column={3} labelStyle={{ textAlign: 'center' }}>
        <Descriptions.Item label={`* ${t('common.label.1')}`} span={3}>
          <SelectEduStep
            eduStepCode={eduStepCode}
            setEduStepCode={(value) => {
              setEduStepCode(value);
              setExamDiv(undefined);
              setExamTypeCode(undefined);
              setSkillExams([_.clone(InitialSkillExam)]);
            }}
            width={150}
            disabled={Boolean(examCode)}
          />
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('cbt.test.column3')}`} span={3}>
          <div className="w-150p">
            <Select
              className="w-100"
              placeholder={t('common.button.select')}
              value={examDiv}
              onChange={(value) => {
                setExamDiv(value);
                onSetExamTypes(eduStepCode, value);
              }}
              disabled={Boolean(examCode)}
            >
              {TE0001?.map((item) => {
                if (
                  item.additionInfo1 === eduStepCode ||
                  item.additionInfo2 === eduStepCode
                ) {
                  return (
                    <Select.Option
                      key={item.detailCode}
                      value={item.detailCode}
                    >
                      {item.korName}
                    </Select.Option>
                  );
                } else {
                  return null;
                }
              })}
            </Select>
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('cbt.test.column4')}`} span={3}>
          <div className="w-150p">
            <Select
              className="w-100"
              placeholder={t('common.button.select')}
              value={examTypeCode}
              onChange={(value) => setExamTypeCode(value)}
              // disabled={!examDiv || openYn === 'Y'}
              // 2024.03.25 시험유형 수정 disabled
              disabled={Boolean(examCode)}
            >
              {examTypes.map((type) => (
                <Select.Option
                  key={type.examTypeCode}
                  value={type.examTypeCode}
                >
                  {type.examTypeName}
                </Select.Option>
              ))}
            </Select>
          </div>
        </Descriptions.Item>
        <Descriptions.Item
          label={eduStepCode === 'EM' ? t('cbt.test.column5') : t('cbt.test.1')}
          span={3}
        >
          {skillExams.map((item, index) => (
            <div key={index} className="skill-exam-form comm-gap">
              <input
                type="text"
                className="form-control"
                value={eduStepCode === 'EM' ? '0' : String(item.srtScore)}
                onChange={(e) => {
                  onSetSkillExamScore(index, 'srtScore', e.target.value);
                }}
                disabled={eduStepCode === 'EM'}
              />
              <span>~</span>
              <input
                type="text"
                className="form-control"
                value={eduStepCode === 'EM' ? '100' : String(item.endScore)}
                onChange={(e) => {
                  onSetSkillExamScore(index, 'endScore', e.target.value);
                }}
                disabled={eduStepCode === 'EM'}
              />
              {(item.speakingExamName) && <span>{item.speakingExamName}</span>}
              <div className="comm-btn__area">
                <Button
                  className="btn-basic small black"
                  onClick={() => {
                    setSkillExamIndex(index);
                    setSelectSkillExamVisible(true);
                  }}
                  text={t('common.button.search')}
                  // 임시해제
                  // disabled={openYn === 'Y'}
                />
                
                {eduStepCode === 'MD' ? (
                  index === 0 ? (
                    <Button
                      className="btn-basic small pink-line"
                      onClick={addSkillExam}
                      text={t('cbt.test.2')}
                    />
                  ) : (
                    <Button
                      className="btn-basic small pink-line"
                      onClick={() => deleteSkillExam(index)}
                      text={t('common.button.delete')}
                    />
                  )
                ) : null}
              </div>
            </div>
          ))}
        </Descriptions.Item>
        <Descriptions.Item label={t('cbt.test.column6')} span={3}>
          <input
            type="text"
            className="form-control"
            value={examName}
            onChange={(e) => setExamName(e.target.value)}
          />
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('cbt.test.column7')}`}>
          <div className="w-100p">
            <Select
              className="w-120p"
              placeholder={t('common.button.select')}
              value={year}
              onChange={(value) => {
                setYear(value);
                onSetSemesterList(value);
              }}
              disabled={openYn === 'Y'}
            >
              {getReverseYears(SY0005).map((item) => (
                <Select.Option key={item.detailCode} value={item.detailCode}>
                  {item.korName}
                </Select.Option>
              ))}
            </Select>
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('common.column.semester')}`} className='w-120p'>
          <div className="w-240p">
            <Select
              className="w-100p"
              value={semesterType}
              onChange={value => {
                setSemesterType(value);
                onSetSemesterListByType(value);
              }}
              disabled={!year || openYn === 'Y'}
            >
              {
                PR0002?.map(({korName: label, detailCode: value}) => {
                  return (
                    <Select.Option key={value} value={value}>
                      {label}
                    </Select.Option>
                  )
                })
              }
            </Select>
            <Select
              className="w-120p ml-3"
              placeholder={t('common.button.select')}
              value={semesterCode}
              onChange={(value) => setSemesterCode(value)}
              disabled={!year || openYn === 'Y'}
            >
              {examSemesterList.map((item) => (
                <Select.Option
                  key={item.semesterCode}
                  value={item.semesterCode}
                >
                  {item.semesterName}
                </Select.Option>
              ))}
            </Select>
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('cbt.test.column8')}`}>
          <div className="w-100p">
            <Select
              className="w-100"
              placeholder={t('common.button.select')}
              value={testSeq}
              onChange={(value) => setTestSeq(value)}
              disabled={!semesterCode || openYn === 'Y' || semesterType === 'SC'}
            >
              {res2?.data?.map((item) => (
                <Select.Option key={item.monthSeq} value={item.monthSeq}>
                  {item.monthName}
                  {t('payment.charge.text4')}
                </Select.Option>
              ))}
            </Select>
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={t('cbt.test.column2')} span={3}>
          {openYn}
        </Descriptions.Item>
        {examCode && attachFile ? (
          <Descriptions.Item label={t('cbt.test.column9')} span={3}>
            {attachFile?.map((item: any, index: number) => {
              const result = item?.originalName ? (
                <div>
                  <a
                    className="download-link"
                    href={`${item.fileLocation}`}
                    download={item.originalName}
                  >
                    {item.originalName}
                  </a>
                  <span
                    className="ml-1 pointer"
                    onClick={() => onDeleteAttachedFile(index)}
                  >
                    <CgCloseR />
                  </span>
                </div>
              ) : null;
              return result;
            })}
          </Descriptions.Item>
        ) : null}
        <Descriptions.Item
          label={
            examCode && attachFile
              ? t('cbt.test.column10')
              : t('cbt.test.column11')
          }
          span={3}
        >
          {files.length > 0
            ? files.map((file: any, index: number) => (
                <div className="flex mb-2" key={index}>
                  <FileUpload
                    file={files[index]}
                    setFile={(file: any) => onSetFile(index, file)}
                  />
                  <div className="comm-btn__area">
                    <Button
                      className="btn-basic small gray ml-2"
                      onClick={() => onDeleteFile(index)}
                      text={t('common.button.delete')}
                    />
                  </div>
                </div>
              ))
            : null}
          <div className="comm-btn__area">
            <Button
              className="btn-basic pink-line"
              onClick={onAddFile}
              text={t('common.button.add')}
            />
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={t('cbt.type.column4')}>
          <input
            type="text"
            className="form-control"
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
          />
        </Descriptions.Item>
      </Descriptions>
      </div>

      <div className="comm-bx__space">
        <div className="comm-btn__area right">
          {openYn === 'Y' ? (
            <Button
              className="btn-basic pink-line"
              onClick={() => onOpenExam('N')}
              text={t('cbt.test.button3')}
              disabled={!examCode}
              writeAuthYn={writeAuthYn}
            />
          ) : (
            <Button
              className="btn-basic pink-line"
              onClick={() => setPublishExamVisible(true)}
              text={t('cbt.test.button2')}
              disabled={!examCode}
              writeAuthYn={writeAuthYn}
            />
          )}
          <Button
            className="btn-basic pink"
            onClick={onSubmit}
            text={t('common.button.save')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      </div>

      {publishExamVisible && (
        <ConfirmModal
          visible={publishExamVisible}
          setVisible={setPublishExamVisible}
          onConfirm={() => onOpenExam('Y')}
          width={420}
          titleText={t('cbt.test.title2')}
          messageText={t('cbt.test.text2')}
          confirmText={t('cbt.test.button2')}
          cancelText={t('common.button.cancel')}
        />
      )}
      {selectSkillExamVisible && (
        <SelectSkillExamModal
          visible={selectSkillExamVisible}
          setVisible={setSelectSkillExamVisible}
          setSkillExam={setSkillExam}
          eduStepCode={eduStepCode}
          title={eduStepCode === 'MD' ? t('cbt.test.3') : undefined}
        />
      )}
    </Block>
  );
};

export default TestExam;
